import { Vec3, clamp, lerp, wrapAngle } from './math.js';

// A track ribbon: centripetal Catmull-Rom spline through control points, resampled at
// even arc-length spacing, with rotation-minimizing frames (double-reflection) so the
// surface behaves through loops and corkscrews, plus authored roll (banking) on top.

export interface SplinePoint {
  x: number;
  y: number;
  z: number;
  /** Full track width in meters at this control point. */
  width?: number;
  /** Additional roll (radians) applied around the tangent, on top of the RMF frame. */
  roll?: number;
}

export interface Frame {
  pos: Vec3;
  tangent: Vec3;
  up: Vec3;
  right: Vec3;
  width: number;
}

export function makeFrame(): Frame {
  return { pos: new Vec3(), tangent: new Vec3(0, 0, 1), up: new Vec3(0, 1, 0), right: new Vec3(1, 0, 0), width: 24 };
}

export interface Projection {
  /** Arc length position along the spline in meters (0..length). */
  s: number;
  /** Signed lateral offset from centerline in meters (+ = right). */
  lateral: number;
  /** Signed height above the surface plane in meters (+ = above). */
  height: number;
  /** Squared distance to the centerline sample used. */
  distSq: number;
}

const SAMPLE_SPACING = 2; // meters between stored samples

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number, t2: number, t3: number): number {
  const v0 = (p2 - p0) * 0.5;
  const v1 = (p3 - p1) * 0.5;
  return (2 * p1 - 2 * p2 + v0 + v1) * t3 + (-3 * p1 + 3 * p2 - 2 * v0 - v1) * t2 + v0 * t + p1;
}

export class SampledSpline {
  readonly closed: boolean;
  readonly length: number;
  readonly count: number;
  readonly spacing = SAMPLE_SPACING;
  // Per-sample data (xyz interleaved)
  readonly pos: Float32Array;
  readonly tan: Float32Array;
  readonly up: Float32Array;
  readonly width: Float32Array;

  private grid = new Map<number, number[]>();
  private static GRID_CELL = 24;

  constructor(points: SplinePoint[], closed: boolean, defaultWidth = 24) {
    if (points.length < 4) throw new Error('spline needs at least 4 control points');
    this.closed = closed;

    // --- 1. Dense presample with centripetal-ish parameterization ---
    const n = points.length;
    const segCount = closed ? n : n - 3; // open splines use first/last as tangent handles
    const DENSE_PER_SEG = 64;
    const dense: { p: Vec3; seg: number; t: number }[] = [];
    for (let seg = 0; seg < segCount; seg++) {
      const i0 = closed ? (seg - 1 + n) % n : seg;
      const i1 = closed ? seg : seg + 1;
      const i2 = closed ? (seg + 1) % n : seg + 2;
      const i3 = closed ? (seg + 2) % n : seg + 3;
      const p0 = points[i0], p1 = points[i1], p2 = points[i2], p3 = points[i3];
      const last = seg === segCount - 1 && !closed;
      const steps = last ? DENSE_PER_SEG + 1 : DENSE_PER_SEG;
      for (let k = 0; k < steps; k++) {
        const t = k / DENSE_PER_SEG;
        const t2 = t * t, t3 = t2 * t;
        dense.push({
          p: new Vec3(
            catmullRom(p0.x, p1.x, p2.x, p3.x, t, t2, t3),
            catmullRom(p0.y, p1.y, p2.y, p3.y, t, t2, t3),
            catmullRom(p0.z, p1.z, p2.z, p3.z, t, t2, t3)
          ),
          seg,
          t,
        });
      }
    }

    // --- 2. Resample at even arc length ---
    let total = 0;
    const cum = new Float64Array(dense.length);
    for (let i = 1; i < dense.length; i++) {
      total += dense[i].p.distanceTo(dense[i - 1].p);
      cum[i] = total;
    }
    if (closed) total += dense[dense.length - 1].p.distanceTo(dense[0].p);
    this.length = total;
    const count = Math.max(8, Math.round(total / SAMPLE_SPACING));
    this.count = count;
    const step = total / (closed ? count : count - 1);

    this.pos = new Float32Array(count * 3);
    this.tan = new Float32Array(count * 3);
    this.up = new Float32Array(count * 3);
    this.width = new Float32Array(count);
    const segOf = new Float32Array(count); // control segment + t, for roll/width interpolation

    let di = 0;
    for (let i = 0; i < count; i++) {
      const target = Math.min(i * step, total - 1e-6);
      while (di < dense.length - 1 && cum[di + 1] < target) di++;
      const d0 = cum[di];
      const d1 = di < dense.length - 1 ? cum[di + 1] : total;
      const f = d1 > d0 ? (target - d0) / (d1 - d0) : 0;
      const a = dense[di];
      const b = dense[Math.min(di + 1, dense.length - 1)];
      this.pos[i * 3] = lerp(a.p.x, b.p.x, f);
      this.pos[i * 3 + 1] = lerp(a.p.y, b.p.y, f);
      this.pos[i * 3 + 2] = lerp(a.p.z, b.p.z, f);
      segOf[i] = a.seg + clamp(lerp(a.t, b.seg > a.seg ? 1 : b.t, f), 0, 0.9999);
    }

    // --- 3. Tangents from central differences ---
    const tmpA = new Vec3();
    for (let i = 0; i < count; i++) {
      const iPrev = closed ? (i - 1 + count) % count : Math.max(0, i - 1);
      const iNext = closed ? (i + 1) % count : Math.min(count - 1, i + 1);
      tmpA.set(
        this.pos[iNext * 3] - this.pos[iPrev * 3],
        this.pos[iNext * 3 + 1] - this.pos[iPrev * 3 + 1],
        this.pos[iNext * 3 + 2] - this.pos[iPrev * 3 + 2]
      ).normalize();
      this.tan[i * 3] = tmpA.x;
      this.tan[i * 3 + 1] = tmpA.y;
      this.tan[i * 3 + 2] = tmpA.z;
    }

    // --- 4. Rotation-minimizing frames (double reflection) ---
    const ups: Vec3[] = new Array(count);
    const t0 = new Vec3(this.tan[0], this.tan[1], this.tan[2]);
    let u0 = new Vec3(0, 1, 0);
    if (Math.abs(t0.dot(u0)) > 0.95) u0 = new Vec3(1, 0, 0);
    // Orthogonalize
    u0.addScaled(t0, -t0.dot(u0)).normalize();
    ups[0] = u0;
    const xi = new Vec3(), ti = new Vec3(), tn = new Vec3(), v1 = new Vec3(), v2 = new Vec3();
    const uL = new Vec3(), tL = new Vec3();
    for (let i = 0; i < count - 1 + (closed ? 1 : 0); i++) {
      const j = (i + 1) % count;
      xi.set(this.pos[j * 3] - this.pos[i * 3], this.pos[j * 3 + 1] - this.pos[i * 3 + 1], this.pos[j * 3 + 2] - this.pos[i * 3 + 2]);
      ti.set(this.tan[i * 3], this.tan[i * 3 + 1], this.tan[i * 3 + 2]);
      tn.set(this.tan[j * 3], this.tan[j * 3 + 1], this.tan[j * 3 + 2]);
      const c1 = xi.lengthSq();
      if (c1 < 1e-12) {
        if (j !== 0) ups[j] = ups[i].clone();
        continue;
      }
      // reflect u_i and t_i across plane normal to xi
      v1.copy(ups[i]).addScaled(xi, (-2 * xi.dot(ups[i])) / c1);
      v2.copy(ti).addScaled(xi, (-2 * xi.dot(ti)) / c1);
      // reflect across plane normal to (t_next - v2)
      tL.copy(tn).sub(v2);
      const c2 = tL.lengthSq();
      uL.copy(v1);
      if (c2 > 1e-12) uL.addScaled(tL, (-2 * tL.dot(v1)) / c2);
      // orthogonalize against tangent to fight drift
      uL.addScaled(tn, -tn.dot(uL)).normalize();
      if (j === 0) {
        // closed loop: measure mismatch between propagated frame and the initial frame
        const right0 = new Vec3().cross(ups[0], t0); // note: right = up x tangent order fixed below
        const angle = Math.atan2(uL.dot(right0), uL.dot(ups[0]));
        // distribute -angle over the whole loop
        for (let k = 0; k < count; k++) {
          const a = (-angle * k) / count;
          rotateAroundTangent(ups[k], this.tan, k, a);
        }
      } else {
        ups[j] = uL.clone();
      }
    }

    // --- 5. Apply authored roll + width ---
    const rollOf = (idx: number): number => points[idx]?.roll ?? 0;
    const widthOf = (idx: number): number => points[idx]?.width ?? defaultWidth;
    for (let i = 0; i < count; i++) {
      const st = segOf[i];
      const seg = Math.floor(st);
      const t = st - seg;
      // control point indices this segment interpolates between
      const ia = closed ? seg % n : seg + 1;
      const ib = closed ? (seg + 1) % n : Math.min(seg + 2, n - 1);
      const smoothT = t * t * (3 - 2 * t);
      let roll: number;
      if (closed) {
        // interpolate roll along shortest path so ±PI authored twists work
        const ra = rollOf(ia);
        roll = ra + wrapDelta(rollOf(ib) - ra) * smoothT;
      } else {
        roll = lerp(rollOf(ia), rollOf(ib), smoothT);
      }
      this.width[i] = lerp(widthOf(ia), widthOf(ib), smoothT);
      if (roll !== 0) rotateAroundTangent(ups[i], this.tan, i, roll);
      this.up[i * 3] = ups[i].x;
      this.up[i * 3 + 1] = ups[i].y;
      this.up[i * 3 + 2] = ups[i].z;
    }

    // --- 6. Spatial grid for cold nearest-point queries ---
    const cell = SampledSpline.GRID_CELL;
    for (let i = 0; i < count; i++) {
      const key = gridKey(
        Math.floor(this.pos[i * 3] / cell),
        Math.floor(this.pos[i * 3 + 1] / cell),
        Math.floor(this.pos[i * 3 + 2] / cell)
      );
      let arr = this.grid.get(key);
      if (!arr) this.grid.set(key, (arr = []));
      arr.push(i);
    }
  }

  wrapS(s: number): number {
    if (this.closed) {
      s = s % this.length;
      if (s < 0) s += this.length;
      return s;
    }
    return clamp(s, 0, this.length - 1e-3);
  }

  /** Interpolated frame at arc position s (meters). */
  frameAt(s: number, out: Frame): Frame {
    s = this.wrapS(s);
    const f = s / this.spacing;
    let i = Math.floor(f);
    let t = f - i;
    if (i >= this.count) {
      i = this.count - 1;
      t = 0;
    }
    const j = this.closed ? (i + 1) % this.count : Math.min(i + 1, this.count - 1);
    const p = this.pos, tn = this.tan, up = this.up;
    out.pos.set(
      lerp(p[i * 3], p[j * 3], t),
      lerp(p[i * 3 + 1], p[j * 3 + 1], t),
      lerp(p[i * 3 + 2], p[j * 3 + 2], t)
    );
    out.tangent.set(
      lerp(tn[i * 3], tn[j * 3], t),
      lerp(tn[i * 3 + 1], tn[j * 3 + 1], t),
      lerp(tn[i * 3 + 2], tn[j * 3 + 2], t)
    ).normalize();
    out.up.set(
      lerp(up[i * 3], up[j * 3], t),
      lerp(up[i * 3 + 1], up[j * 3 + 1], t),
      lerp(up[i * 3 + 2], up[j * 3 + 2], t)
    );
    // re-orthogonalize up against tangent
    out.up.addScaled(out.tangent, -out.tangent.dot(out.up)).normalize();
    out.right.cross(out.up, out.tangent).normalize();
    out.width = lerp(this.width[i], this.width[j], t);
    return out;
  }

  /** Curvature estimate (1/m) at arc position s. */
  curvatureAt(s: number): number {
    const i = Math.floor(this.wrapS(s) / this.spacing);
    const iPrev = this.closed ? (i - 1 + this.count) % this.count : Math.max(0, i - 1);
    const iNext = this.closed ? (i + 1) % this.count : Math.min(this.count - 1, i + 1);
    const dx = this.tan[iNext * 3] - this.tan[iPrev * 3];
    const dy = this.tan[iNext * 3 + 1] - this.tan[iPrev * 3 + 1];
    const dz = this.tan[iNext * 3 + 2] - this.tan[iPrev * 3 + 2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz) / (2 * this.spacing);
  }

  /** Signed curvature around the up axis (+ = turning right) at s. */
  signedCurvatureAt(s: number, frame: Frame): number {
    const i = Math.floor(this.wrapS(s) / this.spacing);
    const iPrev = this.closed ? (i - 1 + this.count) % this.count : Math.max(0, i - 1);
    const iNext = this.closed ? (i + 1) % this.count : Math.min(this.count - 1, i + 1);
    const dx = this.tan[iNext * 3] - this.tan[iPrev * 3];
    const dy = this.tan[iNext * 3 + 1] - this.tan[iPrev * 3 + 1];
    const dz = this.tan[iNext * 3 + 2] - this.tan[iPrev * 3 + 2];
    // component of tangent derivative along right
    const mag = (dx * frame.right.x + dy * frame.right.y + dz * frame.right.z) / (2 * this.spacing);
    return mag;
  }

  private tmpFrame = makeFrame();
  private tmpV = new Vec3();

  /**
   * Project a world position onto the spline. With a hint (previous s) this is a fast
   * local search; without, it falls back to the spatial grid.
   */
  project(p: Vec3, hintS: number | undefined, out: Projection): Projection {
    let bestI = -1;
    let bestD = Infinity;
    if (hintS !== undefined) {
      const center = Math.floor(this.wrapS(hintS) / this.spacing);
      const WINDOW = 45; // +-90m of local search — covers a tick of travel at any speed
      for (let o = -WINDOW; o <= WINDOW; o++) {
        let i = center + o;
        if (this.closed) i = ((i % this.count) + this.count) % this.count;
        else if (i < 0 || i >= this.count) continue;
        const dx = p.x - this.pos[i * 3];
        const dy = p.y - this.pos[i * 3 + 1];
        const dz = p.z - this.pos[i * 3 + 2];
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      }
    }
    if (bestI === -1 || bestD > 60 * 60) {
      // cold search through grid neighborhood, widening if needed
      const cell = SampledSpline.GRID_CELL;
      const cx = Math.floor(p.x / cell), cy = Math.floor(p.y / cell), cz = Math.floor(p.z / cell);
      for (let r = 0; r <= 4 && bestI === -1; r++) {
        for (let ox = -r; ox <= r; ox++)
          for (let oy = -r; oy <= r; oy++)
            for (let oz = -r; oz <= r; oz++) {
              if (Math.max(Math.abs(ox), Math.abs(oy), Math.abs(oz)) !== r) continue;
              const arr = this.grid.get(gridKey(cx + ox, cy + oy, cz + oz));
              if (!arr) continue;
              for (const i of arr) {
                const dx = p.x - this.pos[i * 3];
                const dy = p.y - this.pos[i * 3 + 1];
                const dz = p.z - this.pos[i * 3 + 2];
                const d = dx * dx + dy * dy + dz * dz;
                if (d < bestD) {
                  bestD = d;
                  bestI = i;
                }
              }
            }
        if (bestI !== -1) {
          // search one more ring to be safe, then stop
          const rr = r + 1;
          for (let ox = -rr; ox <= rr; ox++)
            for (let oy = -rr; oy <= rr; oy++)
              for (let oz = -rr; oz <= rr; oz++) {
                if (Math.max(Math.abs(ox), Math.abs(oy), Math.abs(oz)) !== rr) continue;
                const arr = this.grid.get(gridKey(cx + ox, cy + oy, cz + oz));
                if (!arr) continue;
                for (const i of arr) {
                  const dx = p.x - this.pos[i * 3];
                  const dy = p.y - this.pos[i * 3 + 1];
                  const dz = p.z - this.pos[i * 3 + 2];
                  const d = dx * dx + dy * dy + dz * dz;
                  if (d < bestD) {
                    bestD = d;
                    bestI = i;
                  }
                }
              }
          break;
        }
      }
      if (bestI === -1) {
        // brute force fallback (should effectively never happen)
        for (let i = 0; i < this.count; i++) {
          const dx = p.x - this.pos[i * 3];
          const dy = p.y - this.pos[i * 3 + 1];
          const dz = p.z - this.pos[i * 3 + 2];
          const d = dx * dx + dy * dy + dz * dz;
          if (d < bestD) {
            bestD = d;
            bestI = i;
          }
        }
      }
    }

    // refine: project onto segment toward whichever neighbor is closer along tangent
    const f = this.tmpFrame;
    let s = bestI * this.spacing;
    this.frameAt(s, f);
    const rel = this.tmpV.copy(p).sub(f.pos);
    const along = rel.dot(f.tangent);
    s = this.wrapS(s + clamp(along, -this.spacing, this.spacing));
    this.frameAt(s, f);
    rel.copy(p).sub(f.pos);
    out.s = s;
    out.lateral = rel.dot(f.right);
    out.height = rel.dot(f.up);
    out.distSq = bestD;
    return out;
  }
}

function rotateAroundTangent(up: Vec3, tan: Float32Array, i: number, angle: number): void {
  const tx = tan[i * 3], ty = tan[i * 3 + 1], tz = tan[i * 3 + 2];
  const cos = Math.cos(angle), sin = Math.sin(angle);
  // Rodrigues rotation of `up` around axis t
  const dot = up.x * tx + up.y * ty + up.z * tz;
  const cx = ty * up.z - tz * up.y;
  const cy = tz * up.x - tx * up.z;
  const cz = tx * up.y - ty * up.x;
  up.set(
    up.x * cos + cx * sin + tx * dot * (1 - cos),
    up.y * cos + cy * sin + ty * dot * (1 - cos),
    up.z * cos + cz * sin + tz * dot * (1 - cos)
  );
}

function wrapDelta(a: number): number {
  return wrapAngle(a);
}

function gridKey(x: number, y: number, z: number): number {
  // pack 3 signed ints into one number (21 bits each is overkill; 10 bits is plenty per axis for our worlds)
  return ((x & 0x3ff) << 20) | ((y & 0x3ff) << 10) | (z & 0x3ff);
}
